import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { LineLoaderComponent } from '../line-loader/line-loader.component';
import { SseService } from '../../../api/services/sse.service';
import { ToastrService } from 'ngx-toastr';
import { PollingService } from '../../../api/services/polling.service';
import { environment } from '../../../../environments/environment';
import { Subscription, timer } from 'rxjs';
import { Router } from '@angular/router';

@Component({
  selector: 'app-data-view-with-loader',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    LineLoaderComponent,
  ],
  templateUrl: './data-view-with-loader.component.html',
  styleUrl: './data-view-with-loader.component.css',
})
export class DataViewWithLoaderComponent {
  @Input() formGroup!: FormGroup;
  @Input() fields: any;
  @Input() workflowData: any;
  @Input() sseConfig: any;
  @Input() formId: any;
  @Output() loaderChange = new EventEmitter<boolean>();
  @Output() checkError = new EventEmitter<boolean>();
  loading: boolean = true;
  endpoint = environment.api_endpoint;
  pollingSubscriptions: Subscription[] = [];
  sseConnection?: any;
  cardsData: any[] = [];
  currentStatus: string = '';
  private pollingStarted = false;
  private timeoutRef: any = null;
  private successTriggered = false;

  get isInProgress(): boolean {
    const status = String(
      this.currentStatus ||
        this.fields?.status ||
        this.fields?.value?.status ||
        this.fields?.value ||
        '',
    )
      .toLowerCase()
      .replace(/[-_]/g, ' ')
      .trim();
    return status === 'in progress' || status === 'inprogress';
  }

  private navigateToFailedNode(node: any) {
    const nextNode = node?.next?.find((nxt: any) =>
      nxt.condition.some((condGroup: any) =>
        condGroup.conditions.every((cond: any) => {
          if (cond.value === 'status') {
            return cond.equals.some(
              (eq: any) =>
                String(eq).toLowerCase() === 'failed' ||
                Number(eq) === 400 ||
                Number(eq) === 500,
            );
          }
          if (cond.value === 'data.data.status') {
            return cond.equals.some(
              (eq: any) => String(eq).toLowerCase() === 'failed',
            );
          }
          return false;
        }),
      ),
    );

    const transitionNode = nextNode || node?.next?.[0];

    if (transitionNode) {
      const condGroup = transitionNode.condition[0];
      if (condGroup) {
        const target = condGroup.targetValue;
        const targetType = condGroup.targetType;

        if (targetType === 'node' && target) {
          const urlParts = this.router.url.split('/');
          const workflowIndex = urlParts.indexOf('workflow');
          const workflowId =
            workflowIndex !== -1 ? urlParts[workflowIndex + 1] : null;
          if (workflowId) {
            const currentUrlTree = this.router.parseUrl(this.router.url);
            const accessToken = currentUrlTree.queryParams['accessToken'];
            const navigationExtras = accessToken
              ? { queryParams: { accessToken } }
              : {};

            setTimeout(() => {
              this.router.navigate(
                [`/workflow/${workflowId}/${target}`],
                navigationExtras,
              );
            }, 1000);
          }
        } else if (targetType === 'url' && target) {
          setTimeout(() => {
            let targetUrl =
              target === '/admin/dashboard' || target === 'scc'
                ? '/eciso/executive-dashboard'
                : target;

            if (targetUrl && !targetUrl.startsWith('http')) {
              const isPublicRoute =
                targetUrl.startsWith('/login') ||
                targetUrl.startsWith('/error') ||
                targetUrl.startsWith('/form') ||
                targetUrl.startsWith('/workflow');

              if (!isPublicRoute && !targetUrl.startsWith('/eciso')) {
                targetUrl = targetUrl.startsWith('/')
                  ? `/eciso${targetUrl}`
                  : `/eciso/${targetUrl}`;
              }
            }
            this.router.navigateByUrl(targetUrl);
          }, 1000);
        }
      }
    }
  }

  constructor(
    private toaster: ToastrService,
    private SseService: SseService,
    private pollingService: PollingService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    if (
      this.fields?.status ||
      this.fields?.value?.status ||
      this.fields?.value
    ) {
      this.currentStatus =
        this.fields?.status || this.fields?.value?.status || this.fields?.value;
    }
    if (this.isInProgress) {
      if (this.fields) {
        this.fields.fieldType = 'cards';
      }
      this.setLoading(false);
    }
    if (this.sseConfig) {
      this.loadNodeWithSSE(this.sseConfig, this.formId);
    } else {
      this.stopAllStreams();
      if (
        (this.fields?.fieldType === 'cards' || this.isInProgress) &&
        (this.fields?.status || this.currentStatus)
      ) {
        this.setLoading(false);
      }
    }
  }

  stopAllStreams() {
    this.successTriggered = false;
    this.pollingSubscriptions.forEach((sub) => {
      if (sub && !sub.closed) sub.unsubscribe();
    });
    this.pollingSubscriptions = [];
    if (this.timeoutRef) {
      clearTimeout(this.timeoutRef);
      this.timeoutRef = null;
    }

    // Stop SSE
    if (this.sseConnection) {
      try {
        this.sseConnection.close();
      } catch (err) {
        console.error('Error closing SSE:', err);
      } finally {
        this.sseConnection = null;
      }
    }

    //  FIX: Reset pollingStarted flag
    this.pollingStarted = false;
  }

  ngOnDestroy(): void {
    this.stopAllStreams();
  }

  loadNodeWithSSE(sseConfig: any, formId: any) {
    this.successTriggered = false;
    this.stopAllStreams();

    if (!sseConfig) {
      return;
    }
    console.log(
      '[LoaderComp] loadNodeWithSSE started. Config:',
      sseConfig,
      'formId:',
      formId,
    );

    const node = this.workflowData?.nodes?.find((n: any) => n._id === formId);

    const { timeout, onSuccess, onFailure, onTimeout } = sseConfig;

    const getValueByPath = (source: any, path: string) =>
      path.split('.').reduce((value: any, key: string) => value?.[key], source);

    const hasLocalStorageCondition = (config: any) =>
      config?.condition?.some((group: any) =>
        group.conditions?.some((cond: any) => cond.type === 'localStorage'),
      );

    const isConditionMatched = (
      config: any,
      apiResult: { status: number; data: any },
    ) =>
      config?.condition?.every((group: any) =>
        group.conditions.every((cond: any) => {
          if (cond.type === 'localStorage') {
            const value = localStorage.getItem(cond.value);
            const exists = !!value;

            return cond.equals.includes(exists);
          }

          if (cond.type === 'api') {
            const apiValue =
              cond.value === 'status'
                ? apiResult.status
                : getValueByPath(
                    { status: apiResult.status, data: apiResult.data },
                    cond.value,
                  );

            return cond.equals.some((eq: any) => eq === apiValue);
          }

          return false;
        }),
      );

    const handlePollingResult = (
      config: any,
      apiResult: { status: number; data: any },
      defaultMessageKey: 'onSuccessMessage' | 'onFailureMessage',
      isFailureFlow: boolean = false,
    ) => {
      const nextNode = node?.next?.find((nxt: any) =>
        nxt.condition.some((condGroup: any) =>
          condGroup.conditions.every((cond: any) => {
            if (cond.value === 'status') {
              return cond.equals.some(
                (eq: any) => Number(eq) === Number(apiResult.status),
              );
            }
            return false;
          }),
        ),
      );

      // NEW: Validate config conditions
      const conditionMatched = isConditionMatched(config, apiResult);

      // NEW: Handle targetType=url navigation
      if (
        conditionMatched &&
        config?.targetType === 'url' &&
        config?.targetValue
      ) {
        // stop polling first
        this.stopAllStreams();
        this.setLoading(false);
        this.loaderChange.emit(true);

        this.toaster.success(config?.onSuccessMessage || 'Success', 'Success');
        setTimeout(() => {
          let targetUrl =
            config.targetValue === '/admin/dashboard' ||
            config.targetValue === 'scc'
              ? '/eciso/executive-dashboard'
              : config.targetValue;

          if (targetUrl && !targetUrl.startsWith('http')) {
            // Check if it's a known public or absolute route
            const isPublicRoute =
              targetUrl.startsWith('/login') ||
              targetUrl.startsWith('/error') ||
              targetUrl.startsWith('/form') ||
              targetUrl.startsWith('/workflow');

            // Prepend /eciso if it's an internal route that doesn't already have it
            if (!isPublicRoute && !targetUrl.startsWith('/eciso')) {
              targetUrl = targetUrl.startsWith('/')
                ? `/eciso${targetUrl}`
                : `/eciso/${targetUrl}`;
            }
          }

          this.router
            .navigateByUrl(targetUrl)
            .then((success) => {
              console.log('Navigation Result:', success);
            })
            .catch((err) => {
              console.error('Navigation Error:', err);
            });
        }, 0);

        return;
      } else {
        const rawStatus =
          apiResult?.data?.data?.status ||
          apiResult?.data?.status ||
          apiResult?.data?.data?.data?.status ||
          apiResult?.data?.data?.integrationStatus ||
          apiResult?.data?.integrationStatus ||
          apiResult?.data?.data?.connectionStatus ||
          apiResult?.data?.connectionStatus ||
          apiResult?.data?.data?.state ||
          apiResult?.data?.state;

        const normalizedStatus = rawStatus
          ? String(rawStatus).toLowerCase().replace(/[-_]/g, ' ').trim()
          : '';

        const isInProgressStatus =
          normalizedStatus === 'in progress' ||
          normalizedStatus === 'inprogress' ||
          normalizedStatus === 'pending' ||
          normalizedStatus === 'connecting';

        const isFailedStatus =
          normalizedStatus === 'failed' || normalizedStatus === 'error';

        const isSuccessStatus =
          normalizedStatus === 'success' ||
          normalizedStatus === 'completed' ||
          normalizedStatus === 'connected';

        if (rawStatus) {
          this.currentStatus = rawStatus;
        }

        // If in progress: render in-progress template and continue polling
        if (isInProgressStatus) {
          if (this.fields) {
            this.fields.fieldType = 'cards';
          }
          this.setLoading(false);
          this.loaderChange.emit(true);
          this.pollingStarted = false;
          return;
        }

        // If failed on intermediate poll call: don't navigate immediately, continue polling
        if (isFailedStatus) {
          this.pollingStarted = false;
          return;
        }

        let isSuccess = conditionMatched;

        if (!isSuccess) {
          if (isSuccessStatus) {
            isSuccess = true;
          } else if (
            this.formId === '687f21113da59426ab18a54a' ||
            this.formId === '690deff281121521d33bd02e' ||
            this.formId === '6a04b1dc47fee7be767d9e49' ||
            this.formId === '6a04cdd91c47992c4a7f2490'
          ) {
            isSuccess =
              apiResult.status === 200 &&
              (apiResult.data?.success === true ||
                apiResult.data?.success === 'true');
          }
        }

        if (isSuccess) {
          if (this.isInProgress && this.fields) {
            this.fields.fieldType = 'cards';
          }
          this.successTriggered = true;
          this.stopAllStreams();
          this.toaster.success(
            config?.onSuccessMessage || 'Success',
            'Success',
          );

          // Capture cards structure from API response
          const apiData = apiResult?.data?.data || apiResult?.data;

          if (
            apiData &&
            (apiData.assetCount !== undefined ||
              apiData.taskCount !== undefined ||
              apiData.actionItemCount !== undefined)
          ) {
            this.cardsData = [];
            // Always push asset card (Resources Fetched)
            this.cardsData.push({
              title: 'Resources Fetched',
              value:
                apiData.assetCount !== undefined && apiData.assetCount !== null
                  ? apiData.assetCount
                  : 0,
              type: 'asset',
              description:
                'We scanned and fetched the available resources related to this integration.',
              lastUpdated: apiData.lastUpdated || apiData.last_updated,
            });
            // Always push actionItem card (Action Items Identified)
            this.cardsData.push({
              title: 'Action Items Identified',
              value:
                apiData.actionItemCount !== undefined &&
                apiData.actionItemCount !== null
                  ? apiData.actionItemCount
                  : 0,
              type: 'actionItem',
              description:
                'We identified action items based on best practices and security benchmarks.',
              lastUpdated: apiData.lastUpdated || apiData.last_updated,
            });
            // Conditionally push task card (Tasks Created)
            if (apiData.taskCount !== undefined && apiData.taskCount !== null) {
              this.cardsData.push({
                title: 'Tasks Created',
                value: apiData.taskCount,
                type: 'task',
                description:
                  'We created compliance tasks to guide you through the process.',
                lastUpdated: apiData.lastUpdated || apiData.last_updated,
              });
            }
          } else {
            const rawCards = apiData?.cards || apiData;
            if (Array.isArray(rawCards) && rawCards.length > 0) {
              this.cardsData = rawCards.map((c, idx) => ({
                ...c,
                type:
                  c.type ||
                  (idx % 3 === 0
                    ? 'asset'
                    : idx % 3 === 1
                      ? 'actionItem'
                      : 'task'),
              }));
            } else if (
              rawCards &&
              typeof rawCards === 'object' &&
              Object.keys(rawCards).length > 0
            ) {
              this.cardsData = Object.values(rawCards)
                .filter((val) => val && typeof val === 'object')
                .map((c: any, idx) => ({
                  ...c,
                  type:
                    c.type ||
                    (idx % 3 === 0
                      ? 'asset'
                      : idx % 3 === 1
                        ? 'actionItem'
                        : 'task'),
                }));
            } else {
              this.cardsData = [
                {
                  title: 'Resources Fetched',
                  value: 0,
                  type: 'asset',
                  description:
                    'We scanned and fetched the available resources related to this integration.',
                  lastUpdated: apiData?.lastUpdated || apiData?.last_updated,
                },
                {
                  title: 'Action Items Identified',
                  value: 0,
                  type: 'actionItem',
                  description:
                    'We identified action items based on best practices and security benchmarks.',
                  lastUpdated: apiData?.lastUpdated || apiData?.last_updated,
                },
              ];
            }
          }

          const hasAsset = this.cardsData.some((c) => c.type === 'asset');
          const hasActionItem = this.cardsData.some(
            (c) => c.type === 'actionItem',
          );

          if (!hasAsset) {
            this.cardsData.push({
              title: 'Resources Fetched',
              value: 0,
              type: 'asset',
              description:
                'We scanned and fetched the available resources related to this integration.',
              lastUpdated: apiData?.lastUpdated || apiData?.last_updated,
            });
          }

          if (!hasActionItem) {
            this.cardsData.push({
              title: 'Action Items Identified',
              value: 0,
              type: 'actionItem',
              description:
                'We identified action items based on best practices and security benchmarks.',
              lastUpdated: apiData?.lastUpdated || apiData?.last_updated,
            });
          }

          // Sort cards to ensure 'asset' is first and 'actionItem' is second
          this.cardsData.sort((a, b) => {
            const order: { [key: string]: number } = {
              asset: 1,
              actionItem: 2,
              task: 3,
            };
            return (order[a.type] || 99) - (order[b.type] || 99);
          });

          this.setLoading(false);
          this.loaderChange.emit(true);
        }
      }

      //  FIX: Reset flag after result
      this.pollingStarted = false;
    };

    const startPollingForConfig = (
      config: any,
      defaultMessageKey: 'onSuccessMessage' | 'onFailureMessage',
      isFailureFlow: boolean = false,
    ) => {
      console.log(
        '[LoaderComp] startPollingForConfig called. Config:',
        config,
        'isFailureFlow:',
        isFailureFlow,
      );
      if (!config?.apiUrl || this.pollingStarted) {
        console.log(
          '[LoaderComp] Polling not started. config.apiUrl exists:',
          !!config?.apiUrl,
          'pollingStarted:',
          this.pollingStarted,
        );
        return false;
      }
      this.pollingStarted = true;
      const sub = this.pollingService
        .startPolling(config, config.apiUrl, formId)
        .subscribe({
          next: (apiResult) => {
            handlePollingResult(
              config,
              apiResult,
              defaultMessageKey,
              isFailureFlow,
            );
          },

          error: (err) => {
            this.pollingStarted = false;
            if (!this.successTriggered) {
              this.toaster.error(config?.onFailureMessage || 'Error', 'Error');
              this.setLoading(true);
              this.checkError.emit();
              this.loaderChange.emit(false);
              this.navigateToFailedNode(node);
            }
          },

          complete: () => {
            this.pollingStarted = false;
            if (!this.successTriggered) {
              this.toaster.error(config?.onFailureMessage || 'Error', 'Error');
              this.setLoading(true);
              this.checkError.emit();
              this.loaderChange.emit(false);
              this.navigateToFailedNode(node);
            }
          },
        });

      this.pollingSubscriptions.push(sub);

      return true;
    };

    const startLocalStoragePollingForConfig = (
      config: any,
      defaultMessageKey: 'onSuccessMessage' | 'onFailureMessage',
      isFailureFlow: boolean = false,
    ) => {
      if (!config || !hasLocalStorageCondition(config) || this.pollingStarted) {
        return false;
      }

      this.pollingStarted = true;

      const maxAttempts = config.maxAttempts || 10;
      const intervalMs = config.intervalMs || 5000;
      let attempts = 0;
      const emptyResult = { status: 0, data: {} };

      const sub = timer(0, intervalMs).subscribe(() => {
        attempts++;

        if (isConditionMatched(config, emptyResult)) {
          handlePollingResult(
            config,
            emptyResult,
            defaultMessageKey,
            isFailureFlow,
          );
          sub.unsubscribe();
          return;
        }

        if (attempts >= maxAttempts) {
          if (!this.successTriggered) {
            this.toaster.error(config?.onFailureMessage || 'Error', 'Error');
            this.setLoading(true);
            this.checkError.emit();
            this.loaderChange.emit(false);
            this.navigateToFailedNode(node);
          }
          this.pollingStarted = false;
          sub.unsubscribe();
        }
      });

      this.pollingSubscriptions.push(sub);

      return true;
    };

    const emptyResult = { status: 0, data: {} };
    const directConditionConfigs = [
      {
        config: onFailure,
        messageKey: 'onFailureMessage' as const,
        isFailureFlow: true,
      },
      {
        config: onSuccess,
        messageKey: 'onSuccessMessage' as const,
        isFailureFlow: false,
      },
      {
        config: onTimeout,
        messageKey: 'onFailureMessage' as const,
        isFailureFlow: true,
      },
    ];

    const matchedDirectConfig = directConditionConfigs.find(
      ({ config }) =>
        hasLocalStorageCondition(config) &&
        isConditionMatched(config, emptyResult),
    );

    if (matchedDirectConfig?.config) {
      handlePollingResult(
        matchedDirectConfig.config,
        emptyResult,
        matchedDirectConfig.messageKey,
        matchedDirectConfig.isFailureFlow,
      );
      return;
    }

    //  Timeout drives the fallback flow instead of opening/using the SSE URL.
    if (timeout && onTimeout) {
      console.log(
        '[LoaderComp] Registering fallback timeout:',
        timeout,
        'ms for config:',
        onTimeout,
      );
      this.timeoutRef = setTimeout(() => {
        console.log(
          '[LoaderComp] Timeout triggered. Calling fallback polling.',
        );
        if (
          startLocalStoragePollingForConfig(
            onTimeout,
            'onFailureMessage',
            true,
          ) ||
          startPollingForConfig(onTimeout, 'onFailureMessage', true)
        ) {
          return;
        }
      }, timeout);
      return;
    }

    if (
      startLocalStoragePollingForConfig(onSuccess, 'onSuccessMessage', false) ||
      startLocalStoragePollingForConfig(onFailure, 'onFailureMessage', true) ||
      startPollingForConfig(onSuccess, 'onSuccessMessage', false) ||
      startPollingForConfig(onFailure, 'onFailureMessage', true)
    ) {
      return;
    }
  }

  setLoading(value: boolean) {
    this.loading = value;
  }

  stopPolling() {
    this.stopAllStreams();
  }
}
